import { Button } from "@ui/Button";
import { useCollapsibleBar } from "@ui/hooks/useCollapsibleBar";
import { HStack } from "@ui/Stack";
import { Tooltip, TooltipTrigger } from "@ui/Tooltip";
import { Text } from "@ui/typography/Text";
import { useIsMobile } from "@utils/responsive";
import { createEffect, onCleanup, Show } from "solid-js";
import { Dynamic } from "solid-js/web";

type Props = {
	displayName: string;
	avatarUrl: string | null;
	/** Creator-uploaded 5:1 web banner; falls back to var(--minto-gordo) when unset. */
	bannerUrl?: string | null;
	tagline?: string;
	bio?: string | null;
	/** Thread card: 11.875rem tall, banner half, avatar centered on the card. */
	compact?: boolean;
	/** Creator public profile; adds a trailing arrow link beside the header. */
	profileUrl?: string;
};

/**
 * Ask-page hero: banner + identity header (Task 4.1a,
 * tasks/ask-page-decoupling-plan.md; banner/avatar overlap added by the
 * public ask-page redesign). Deliberately not @ui/Header — that component
 * pulls in Studio-only help-panel cards from
 * ~/modules/askgvt/studio/help/components/*, which would drag Studio-only
 * code into this public bundle.
 */
export const AskPageHeader = (props: Props) => {
	const avatar = (
		<Dynamic
			component={props.profileUrl ? "a" : "div"}
			class="ask-page__avatar"
			href={props.profileUrl}
			aria-label={
				props.profileUrl ? `View ${props.displayName}'s profile` : undefined
			}
			aria-hidden={props.profileUrl ? undefined : "true"}
		>
			<Show
				when={props.avatarUrl}
				fallback={props.displayName.charAt(0).toUpperCase()}
			>
				{(url) => (
					<img
						class="ask-page__avatar-image"
						src={url()}
						alt=""
						width={72}
						height={72}
					/>
				)}
			</Show>
		</Dynamic>
	);

	const isMobile = useIsMobile();
	const collapsible = () => Boolean(props.compact) && isMobile();

	const header = (
		<header class="ask-page__header">
			<Show when={!props.compact}>{avatar}</Show>
			<div class="ask-page__identity">
				<Text
					as="span"
					size={collapsible() ? "intermediate" : "medium-large"}
					weight="bold"
					color="ink"
				>
					{props.displayName}
				</Text>
				<Text as="span" family="mono" size="small" color="fg3">
					{props.tagline ?? "Ask me anything"}
				</Text>
				<Show when={props.bio}>
					{(bio) => <p class="ask-page__bio">{bio()}</p>}
				</Show>
			</div>
		</header>
	);

	let bar: HTMLDivElement | undefined;
	const collapser = useCollapsibleBar({
		bar: () => bar,
		enabled: collapsible,
		collapsedHeightProperty: "--ask-page-hero-collapsed-bar",
	});

	createEffect(() => {
		if (!collapsible()) {
			return;
		}

		// On mobile the document scrolls, so everything pinned in <main> has to
		// sit below the layout's own top bar, and the bar below the signed-in
		// title header too. Waits a frame so the hero is in the document.
		let observer: ResizeObserver | undefined;
		let main: HTMLElement | null | undefined;
		const frame = requestAnimationFrame(() => {
			main = bar?.closest("main");
			if (!main) {
				return;
			}
			const pinned = main;
			const chrome = pinned
				.closest(".askgvt1")
				?.querySelector<HTMLElement>(":scope > header");
			const titleHeader = pinned.querySelector<HTMLElement>(
				":scope > header.gvtui-header",
			);
			// Fractional heights: offsetHeight rounds to whole pixels, which on
			// fractional device pixel ratios leaves a hairline between the
			// pinned headers.
			const measure = () => {
				const chromeHeight = chrome?.getBoundingClientRect().height ?? 0;
				const titleHeight = titleHeader?.getBoundingClientRect().height ?? 0;
				pinned.style.setProperty("--ask-page-chrome-top", `${chromeHeight}px`);
				pinned.style.setProperty(
					"--ask-page-hero-top",
					`${chromeHeight + titleHeight}px`,
				);
				collapser.recheck();
			};
			observer = new ResizeObserver(measure);
			for (const header of [chrome, titleHeader]) {
				if (header) {
					observer.observe(header);
				}
			}
			measure();
		});
		onCleanup(() => {
			cancelAnimationFrame(frame);
			observer?.disconnect();
			main?.style.removeProperty("--ask-page-chrome-top");
			main?.style.removeProperty("--ask-page-hero-top");
		});
	});

	return (
		<div
			ref={bar}
			classList={{
				"ask-page__hero-bar": collapsible(),
				"ask-page__hero-bar--collapsed": collapser.collapsed(),
				"ask-page__hero-bar--holds-space": collapser.holdsSpace(),
			}}
		>
			<div
				classList={{
					"ask-page__hero": true,
					"ask-page__hero--compact": props.compact,
				}}
			>
				<div
					class="ask-page__banner"
					style={
						props.bannerUrl
							? { "background-image": `url("${props.bannerUrl}")` }
							: undefined
					}
				/>
				<Show when={props.compact}>{avatar}</Show>
				<Show when={props.profileUrl} fallback={header}>
					{(url) => (
						<HStack spacing="large" align="center" class="ask-page__hero-row">
							{header}
							<TooltipTrigger
								class="ask-page__profile-link-trigger"
								placement="bottom end"
							>
								<Button
									as="a"
									href={url()}
									variant="ghost"
									theme="monochrome"
									shape="square"
									symbol="chevron_right"
									symbolRelativeSize={1.25}
									class="ask-page__profile-link"
									aria-label={`View ${props.displayName}'s profile`}
								/>
								<Tooltip>View {props.displayName}'s profile</Tooltip>
							</TooltipTrigger>
						</HStack>
					)}
				</Show>
				<Show when={collapsible()}>
					<Button
						variant="text"
						theme="monochrome"
						shape="circle"
						size="small"
						symbol={collapser.collapsed() ? "expand_more" : "expand_less"}
						class="ask-page__hero-toggle"
						aria-expanded={!collapser.collapsed()}
						aria-label={
							collapser.collapsed()
								? `Show ${props.displayName}'s banner`
								: `Hide ${props.displayName}'s banner`
						}
						onClick={collapser.toggle}
					/>
				</Show>
			</div>
		</div>
	);
};
